import { useEffect, Suspense, useContext } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import makeStyles from '@mui/styles/makeStyles'
import { Theme } from '@mui/material/styles'
import { motion, AnimatePresence } from 'framer-motion'
import { useSelector } from 'react-redux'
import { Route, Switch, useParams, useLocation } from 'react-router-dom'
import { PermissionedRoute } from '@authed/permissioned.route'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { DatasetsHeader } from '@components/headers/datasets'
import { MainTabHolder } from '@components/index'
import { INNER_PAGE_HEIGHT } from '@components/layout'
import { Loader } from '@components/loader'
import { MainTabsAreaWithHeader } from '@components/tab-layouts/main-tabs-area'
import { usePageTitleContext } from '@contexts'
import { PageHeaderContext } from '@contexts/pageHeader'
import { colors } from '@global/constants'
import { useFetchApprovalRequests } from '@global/hooks'
import { useApprovalsEnabled } from '@global/hooks/useApprovalsEnabled'
import { useTemplate } from '@global/hooks/useTemplate'
import { lazyImport } from '@global/utils/lazyImport'
import { useAppSelector } from '@redux/hooks'
import { Integrations } from './Integrations'
import { PendingWithProvider } from './Pending'
import { Submissions } from './Submissions'
import { Approval } from './approval'
import { ApprovalContextProvider } from './approval/contexts'
import { Order, OrderBy } from './approval/contexts/approvalContext/types'
import { t } from '@lingui/core/macro'
import { getIsMetaIntegration } from '@utils/isMetaIntegration'
import { ROUTES } from 'routes'

const { SelectiveDataset } = lazyImport(
  () => import(/* webpackChunkName: "selectiveDataset" */ 'datasets/create-dataset/selective'),
)

const { DataRouteControl } = lazyImport(
  () => import(/* webpackChunkName: "dataRouteControl" */ './data-route-control'),
)

const { CampaignHealth, CampaignHealthHeader } = lazyImport(
  () => import(/* webpackChunkName: "campaignHealth" */ './campaign-health'),
)

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    display: 'flex',
    height: 'calc(100vh - 48px)',
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  iconStyles: {
    color: colors.teal.darker,
    margin: '0 6px',
  },
  linkTab: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: 24,
  },
}))

const Holder = () => <div>{t`Holder`}</div>

const SelectiveRoute = () => (
  <Suspense fallback={<Loader />}>
    <div style={{ height: INNER_PAGE_HEIGHT }}>
      <SelectiveDataset />
    </div>
  </Suspense>
)

export const Templates = () => {
  return (
    <Switch>
      <PermissionedRoute exact path="/template/:id/selective" component={SelectiveRoute} />
      <PermissionedRoute path="/template/:id" component={TemplatesPages} />
    </Switch>
  )
}

interface ApprovalRouteControlRouterParams {
  id: string
}

const ApprovalRouteControl = () => {
  const params = useParams<ApprovalRouteControlRouterParams>()
  const { template } = useTemplate({ templateId: params.id })
  const { features } = useAppSelector(state => state.Auth)
  const APPROVAL_ENABLED = Boolean(features.approval)
  const isApprovalsEnabled = useApprovalsEnabled()
  const showApprovalRequests = isApprovalsEnabled && Boolean(template?.settings.require_approval)

  const {
    approvalRequests = [],
    setApprovalRequestsCount,
    fetchApprovalRequests,
  } = useFetchApprovalRequests({ showApprovalRequests, template })

  useEffect(() => {
    if (!template || !APPROVAL_ENABLED) return

    void fetchApprovalRequests({
      orderBy: OrderBy.CREATED_AT,
      order: Order.DESC,
      offset: 0,
    })
  }, [template])

  if (!showApprovalRequests) return null

  return (
    <ApprovalContextProvider
      approvalRequests={approvalRequests}
      fetchApprovalRequests={fetchApprovalRequests}
      setApprovalRequestsCount={setApprovalRequestsCount}
    >
      <Approval />
    </ApprovalContextProvider>
  )
}

interface TemplatesPagesRouterParams {
  id: string
}

const TemplatesPages = () => {
  const classes = useStyles()
  const params = useParams<TemplatesPagesRouterParams>()
  const location = useLocation()

  const { template } = useTemplate({ templateId: params.id })

  const { addPrefix, removePrefix } = usePageTitleContext()
  const { dispatch: pageHeaderDispatch } = useContext(PageHeaderContext)
  const [DATASETS_PAGE_ENABLED = false, CAMPAIGN_HEALTH_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE, FeatureDict.CAMPAIGN_HEALTH]),
  )

  useEffect(() => {
    if (!template?.name) return
    addPrefix(template.name)

    return () => {
      removePrefix()
    }
  }, [template?.name])

  // Set page header configuration
  useEffect(() => {
    const folderName = template?.folder.name

    if (!folderName && !template?.name) return

    pageHeaderDispatch({
      type: 'setBreadcrumbs',
      payload: [
        ...(DATASETS_PAGE_ENABLED
          ? [{ label: t`Datasets`, path: '/' }]
          : [{ label: t`Templates`, path: '/codes/templates' }]),
        folderName ? { label: folderName } : null,
      ].filter(p => p),
    })

    return () => {
      pageHeaderDispatch({
        type: 'setBreadcrumbs',
        payload: [],
      })
    }
  }, [template?.folder.name, template?.name])

  const isMetaIntegration = template && getIsMetaIntegration(template)

  return (
    <motion.div
      className="submissions"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.15 } }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
    >
      <DatasetsHeader templateId={params.id} title={template?.name}>
        <Suspense>
          <Switch key={location.pathname} location={location}>
            {CAMPAIGN_HEALTH_ENABLED && isMetaIntegration && (
              <Route path={ROUTES.TEMPLATE_CAMPAIGN_HEALTH} component={CampaignHealthHeader} />
            )}
          </Switch>
        </Suspense>
      </DatasetsHeader>
      <MainTabHolder>
        <MainTabsAreaWithHeader>
          <div style={{ height: '100%', overflow: 'scroll' }}>
            <AnimatePresence exitBeforeEnter initial={false}>
              <Suspense fallback={<Loader />}>
                <Switch key={location.pathname} location={location}>
                  <Route path={ROUTES.TEMPLATE_DATA} component={DataRouteControl} />
                  <Route path={ROUTES.TEMPLATE_PENDING} component={PendingWithProvider} />
                  <Route path={ROUTES.TEMPLATE_SUBMISSIONS} component={Submissions} />
                  <Route path={ROUTES.TEMPLATE_INTEGRATIONS} component={Integrations} />
                  <Route path={ROUTES.TEMPLATE_SYNC} component={Holder} />
                  <Route
                    path={[ROUTES.TEMPLATE_APPROVAL_REVIEW, ROUTES.TEMPLATE_APPROVAL]}
                    component={ApprovalRouteControl}
                  />
                  {CAMPAIGN_HEALTH_ENABLED && isMetaIntegration && (
                    <Route path={ROUTES.TEMPLATE_CAMPAIGN_HEALTH} component={CampaignHealth} />
                  )}
                  <Route
                    path="*"
                    render={() => (
                      <div className={classes.loadingContainer}>
                        <CircularProgress />
                      </div>
                    )}
                  />
                </Switch>
              </Suspense>
            </AnimatePresence>
          </div>
        </MainTabsAreaWithHeader>
      </MainTabHolder>
    </motion.div>
  )
}
